import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStudentsList } from '../../api/hooks';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import { relativeTime, ENGAGEMENT_LABEL, WELLBEING_LABEL, fullNameOf, Initials, RiskPill, AlertsChip } from '../../components/studentRow';
import EngagementDot from '../../components/EngagementDot';
import WellbeingIndicator from '../../components/WellbeingIndicator';
import { PanelCard, EmptyState, Spinner, ErrorPanel, SortableTh, PillTabs, Segmented, PagedFooter, Table, Td, TextLink, Track } from '../../components/ui';
import { FaUserGraduate } from '../../components/ui/icons';
import { useSortableTable, SortColumn } from '../../lib/useSortableTable';
import { usePagination } from '../../lib/usePagination';
import { shortSection } from '../class_performance/lib/cpBits';
import { rankByAverage } from './sectionMath';
import type { StudentListRow } from '../../api/types';
import { PASS_MARK, TH_RULE } from '../../lib/tableRules';
import { withTime } from '../../lib/dates';

type Filter = 'all' | 'below' | 'risk' | 'flagged';
type SortChoice = 'rank' | 'name' | 'risk' | 'active';


// Artboard "Sort by". Rank needs the per-student average; "Biggest drop" needs a 4-week change the API does not send.
const SORT_CHOICES: Record<SortChoice, { key: string; dir: 'asc' | 'desc' }> = {
  rank: { key: 'avg', dir: 'desc' },
  name: { key: 'name', dir: 'asc' },
  risk: { key: 'risk', dir: 'desc' },
  active: { key: 'last_active', dir: 'desc' },
};

const COLUMNS: SortColumn<StudentListRow>[] = [
  { key: 'name', accessor: fullNameOf },
  { key: 'avg', accessor: (r) => r.avg_score, type: 'number' },
  { key: 'risk', accessor: (r) => r.risk_score, type: 'number' },
  { key: 'engagement', accessor: (r) => r.engagement_dot },
  { key: 'wellbeing', accessor: (r) => r.wellbeing_dot },
  { key: 'open_alerts', accessor: (r) => r.open_alerts_count, type: 'number' },
  { key: 'last_active', accessor: (r) => r.last_active_at, type: 'date' },
];

export default function SectionRoster({ gradeId, sectionId, gradeName, sectionName }: { gradeId: number; sectionId: number; gradeName?: string; sectionName?: string }) {
  const { role } = useAuth();
  const teacherView = role === 'teacher';
  const q = useStudentsList({ grade: gradeId, section: sectionId });
  const [filter, setFilter] = useState<Filter>('all');
  const all = q.data?.results;
  const hasAvg = !!all?.some((r) => r.avg_score != null);
  const atRisk = (r: StudentListRow) => r.risk_band === 'high' || r.risk_band === 'medium';
  const flagged = (r: StudentListRow) => r.open_alerts_count > 0;
  const below = (r: StudentListRow) => r.avg_score != null && r.avg_score < PASS_MARK;
  const filtered = useMemo(
    () => (all ?? []).filter((r) => filter === 'all' || (filter === 'below' ? below(r) : filter === 'risk' ? atRisk(r) : flagged(r))),
    [all, filter],
  );
  const ranks = useMemo(() => rankByAverage(all ?? []), [all]);
  const { rows, sort, toggle, setSort } = useSortableTable(filtered, COLUMNS, { key: 'name', dir: 'asc' });
  // With averages the roster opens as a ranking (best first), as in the design.
  const ranked = useRef(false);
  useEffect(() => {
    if (hasAvg && !ranked.current) { ranked.current = true; setSort(SORT_CHOICES.rank); }
  }, [hasAvg, setSort]);
  const pg = usePagination(rows, 12, `${filter}${sort?.key}${sort?.dir}`);
  const sortChoice = (Object.keys(SORT_CHOICES) as SortChoice[])
    .find((k) => SORT_CHOICES[k].key === sort?.key && SORT_CHOICES[k].dir === sort?.dir) ?? ('' as SortChoice);

  let body;
  if (q.isLoading) body = <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  else if (q.isError || !all) body = <ErrorPanel />;
  else if (all.length === 0) {
    body = <EmptyState icon={<FaUserGraduate />} title="No students in this section yet" body="Assign students to this section from the Students page." />;
  } else {
    body = (
      <>
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2.5 sm:justify-end">
          <PillTabs dense label="Filter roster" value={filter} onChange={setFilter} options={[
            { key: 'all', label: 'All', count: all.length },
            ...(hasAvg ? [{ key: 'below' as const, label: 'Below pass', count: all.filter(below).length, tone: 'danger' as const }] : []),
            { key: 'risk', label: 'At risk', count: all.filter(atRisk).length, tone: 'danger' },
            { key: 'flagged', label: 'Flagged', count: all.filter(flagged).length, tone: 'warning' },
          ]} />
          <div className="flex items-center gap-2.5">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Sort by</span>
            <Segmented<SortChoice> label="Sort by" value={sortChoice} onChange={(k) => setSort(SORT_CHOICES[k])}
              options={[
                ...(hasAvg ? [{ key: 'rank' as const, label: 'Rank' }] : []),
                { key: 'name', label: 'Name A–Z' }, { key: 'risk', label: 'Highest risk' }, { key: 'active', label: 'Recently active' },
              ]} />
          </div>
        </div>
        {rows.length === 0 ? (
          <EmptyState compact icon={<FaUserGraduate />} title="No students match this filter" />
        ) : (
          <Table minWidth={900}>
            <thead>
              <tr>
                {hasAvg && <th scope="col" className={`px-2.5 pb-2.5 text-left font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted ${TH_RULE}`}>#</th>}
                <SortableTh columnKey="name" sort={sort} onToggle={toggle} className={TH_RULE}>Student</SortableTh>
                {hasAvg && <SortableTh columnKey="avg" sort={sort} onToggle={toggle} className={`${TH_RULE} w-[22%]`} help="avg_score">Average</SortableTh>}
                <SortableTh columnKey="engagement" sort={sort} onToggle={toggle} className={TH_RULE} help="engagement_dot">Engagement</SortableTh>
                <SortableTh columnKey="wellbeing" sort={sort} onToggle={toggle} className={TH_RULE} help="wellbeing_dot">Wellbeing</SortableTh>
                <SortableTh columnKey="risk" sort={sort} onToggle={toggle} className={TH_RULE} help="risk_band">Risk</SortableTh>
                <SortableTh columnKey="open_alerts" sort={sort} onToggle={toggle} align="right" className={`${TH_RULE} whitespace-nowrap`} help="open_alerts">Open alerts</SortableTh>
                <SortableTh columnKey="last_active" sort={sort} onToggle={toggle} className={`${TH_RULE} whitespace-nowrap`} help="last_active">Last active</SortableTh>
                <th className={`px-2.5 pb-2.5 ${TH_RULE}`}><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {pg.pageItems.map((r) => {
                const name = fullNameOf(r);
                const rank = ranks.get(r.id);
                const lo = below(r);
                return (
                  <tr key={r.id}>
                    {hasAvg && (
                      <Td>
                        <span className={`grid h-[30px] w-[30px] place-items-center rounded-[10px] font-mono text-[13px] font-semibold ${
                          rank === 1 ? 'bg-navy text-white' : lo ? 'bg-accent-bg text-accent-ink' : 'bg-track text-muted-2'}`}>{rank ?? '—'}</span>
                      </Td>
                    )}
                    <Td className="whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <Initials name={name} />
                        <div className="min-w-0">
                          <Link to={`/students/profile/${r.id}`} className="block font-semibold text-ink no-underline hover:underline hover:underline-offset-[3px]">{name}</Link>
                          <span className="block text-xs text-muted">
                            {lo ? 'Below pass mark' : [r.grade_name, r.section?.name].filter(Boolean).join(' · ') || `@${r.username}`}
                          </span>
                        </div>
                      </div>
                    </Td>
                    {hasAvg && (
                      <Td>
                        {r.avg_score == null ? <span className="text-muted">—</span> : (
                          <div className="flex items-center gap-2.5">
                            <Track value={r.avg_score} tone={lo ? 'accent' : 'slate'} label={`${name} average`} className="min-w-[70px] flex-1" />
                            <span className="w-12 text-right font-mono font-semibold tabular-nums">{r.avg_score.toFixed(1)}%</span>
                          </div>
                        )}
                      </Td>
                    )}
                    <Td className="whitespace-nowrap">
                      <span className="inline-flex items-center gap-2 text-[13px]">
                        <EngagementDot value={r.engagement_dot} />{ENGAGEMENT_LABEL[r.engagement_dot] ?? r.engagement_dot}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap">
                      <span className="inline-flex items-center gap-2 text-[13px]">
                        <WellbeingIndicator value={r.wellbeing_dot} teacherView={teacherView} />
                        {teacherView ? null : (WELLBEING_LABEL[r.wellbeing_dot] ?? r.wellbeing_dot)}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap"><RiskPill r={r} /></Td>
                    <Td align="right" className="whitespace-nowrap tabular-nums"><AlertsChip count={r.open_alerts_count} /></Td>
                    <Td className="whitespace-nowrap text-xs text-muted" title={r.last_active_at ? withTime(r.last_active_at) : undefined}>
                      {relativeTime(r.last_active_at)}
                    </Td>
                    <Td align="right"><TextLink to={`/students/profile/${r.id}`} aria-label={`Open ${name}`}>360 →</TextLink></Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {rows.length > 0 && <PagedFooter divider="list" label="Roster pagination" {...pg} onPage={pg.setPage} />}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2.5">
          <span className="text-[13px] text-muted">{hasAvg ? `Rank is the position in the whole section of ${all.length}` : ''}</span>
          <TextLink to={`/students?grade=${gradeId}&section=${sectionId}`}>
            All {all.length} {all.length === 1 ? 'student' : 'students'} in {shortSection(gradeName, sectionName) || 'this section'} →
          </TextLink>
        </div>
      </>
    );
  }
  return (
    <PanelCard title="Student ranking" align="end" className="mb-[18px]"
      subtitle={hasAvg ? 'Average across all subjects, all time' : undefined}>
      {body}
    </PanelCard>
  );
}
